import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger,
} from "@/components/ui/select";
import { FileText, FolderKanban, ListChecks, ExternalLink } from "lucide-react";

const COLUMNS = [
  { key: "draft", label: "Draft", statuses: ["draft"], accent: "border-t-zinc-400" },
  { key: "sent", label: "Sent", statuses: ["sent", "viewed"], accent: "border-t-sky-500" },
  { key: "negotiation", label: "In Negotiation", statuses: ["internal_review"], accent: "border-t-amber-500" },
  { key: "approved", label: "Approved", statuses: ["accepted"], accent: "border-t-emerald-500" },
  { key: "declined", label: "Declined / Expired", statuses: ["declined", "expired", "archived"], accent: "border-t-red-400" },
];

const MOVE_TARGETS = [
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Sent" },
  { value: "internal_review", label: "In Negotiation" },
  { value: "accepted", label: "Approved" },
  { value: "declined", label: "Declined" },
];

const fmtMoney = (n) =>
  n == null ? null : new Intl.NumberFormat("en-AU", {
    style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(n);

export default function ProposalsPipelinePage() {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/proposals-pipeline");
      setProposals(data.proposals || []);
    } catch { toast.error("Failed to load pipeline"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cols = useMemo(() => COLUMNS.map((c) => {
    const rows = proposals.filter((p) => c.statuses.includes(p.status));
    const value = rows.reduce((sum, p) => {
      const t = p.totals || {};
      return sum + (t.one_off_subtotal || 0) + (t.monthly_subtotal || 0) * 12;
    }, 0);
    return { ...c, rows, value };
  }), [proposals]);

  const move = async (p, status) => {
    try {
      await api.post(`/proposals/${p.proposal_id}/transition`, { status });
      toast.success(status === "accepted"
        ? "Proposal approved — project created and pending tasks activated"
        : "Proposal moved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Move failed"); }
  };

  const createProject = async (p) => {
    try {
      const { data } = await api.post(`/projects/from-proposal/${p.proposal_id}`);
      toast.success("Project created");
      navigate(`/projects/${data.project.project_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Conversion failed"); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="proposals-pipeline-page">
      <div>
        <h1 className="text-2xl font-display font-extrabold tracking-tight flex items-center gap-2">
          <FileText className="h-6 w-6 text-[#002FA7]" /> Proposals — Sales Pipeline
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Approved proposals automatically become Projects and their pending tasks go live.
          Column value = one-off + 12&times; monthly (GST-excl).
        </p>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-zinc-400">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3 items-start">
          {cols.map((col) => (
            <div key={col.key} className={`bg-zinc-50/80 border border-zinc-200 border-t-4 ${col.accent} rounded-sm`}
              data-testid={`pipeline-col-${col.key}`}>
              <div className="px-3 py-2.5 border-b border-zinc-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-widest text-zinc-600">{col.label}</span>
                  <span className="text-xs font-semibold text-zinc-400">{col.rows.length}</span>
                </div>
                <div className="text-[11px] text-zinc-400 mt-0.5">{fmtMoney(col.value)}</div>
              </div>
              <div className="p-2 space-y-2 min-h-[80px]">
                {col.rows.map((p) => (
                  <div key={p.proposal_id}
                    className="bg-white border border-zinc-200 rounded-sm p-3 shadow-sm hover:shadow-md transition-shadow space-y-1.5"
                    data-testid={`pipeline-card-${p.proposal_id}`}>
                    <Link to={`/clients/${p.client_id}/proposals/${p.proposal_id}`}
                      className="text-sm font-semibold text-zinc-800 leading-snug hover:underline flex items-start gap-1">
                      <span className="flex-1">{p.title || "Untitled proposal"}</span>
                      <ExternalLink className="h-3 w-3 text-zinc-300 mt-0.5 shrink-0" />
                    </Link>
                    <div className="text-xs text-zinc-500">{p.client_name}</div>
                    <div className="flex items-center gap-2 text-[11px] text-zinc-400">
                      {p.totals && (
                        <span className="font-semibold text-zinc-600">
                          {fmtMoney((p.totals.one_off_subtotal || 0) + (p.totals.monthly_subtotal || 0) * 12)}
                        </span>
                      )}
                      {p.task_count > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <ListChecks className="h-3 w-3" /> {p.task_count} task{p.task_count > 1 ? "s" : ""}
                        </span>
                      )}
                      <span className="ml-auto">{(p.updated_at || p.created_at || "").slice(0, 10)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 pt-1">
                      <Select value="" onValueChange={(v) => move(p, v)}>
                        <SelectTrigger className="h-6 text-[11px] px-2 w-auto gap-1"
                          data-testid={`pipeline-move-${p.proposal_id}`}>
                          <span className="text-zinc-500">Move to…</span>
                        </SelectTrigger>
                        <SelectContent>
                          {MOVE_TARGETS.filter((t) => t.value !== p.status).map((t) => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {p.status === "accepted" && (
                        p.project_id ? (
                          <Button size="sm" variant="outline" className="h-6 text-[11px] px-2"
                            onClick={() => navigate(`/projects/${p.project_id}`)}
                            data-testid={`pipeline-view-project-${p.proposal_id}`}>
                            <FolderKanban className="h-3 w-3 mr-1" /> Project
                          </Button>
                        ) : (
                          <Button size="sm" className="h-6 text-[11px] px-2"
                            onClick={() => createProject(p)}
                            data-testid={`pipeline-create-project-${p.proposal_id}`}>
                            <FolderKanban className="h-3 w-3 mr-1" /> Create project
                          </Button>
                        )
                      )}
                    </div>
                  </div>
                ))}
                {col.rows.length === 0 && (
                  <div className="text-[11px] text-zinc-300 text-center py-4">Empty</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
